import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties, Ref } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { Status as CustomerStatus } from '@brighterly/lib-core-types/customer/enums'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { useListsStore } from '@shared/model'
import { AppRoute, TasksKanbanRelatedToType } from '@shared/config'

import { DisciplineGeneralModal, DisciplineScheduleFlow } from './enums'
import { useDisciplineStore } from './store'
import { useDisciplineGeneralInfoStore } from './general-info/store'
import { useDisciplineScheduleStore } from './schedule/store'
import { useDisciplinePrivileges } from './discipline-privileges.hook'

interface DisciplineActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  canManageDisciplines: ComputedRef<boolean>
  canDeleteDisciplines: ComputedRef<boolean>
  refIsTasksVisible: ComputedRef<boolean>
  refHasTutor: ComputedRef<boolean>
  toggle: () => void
  openChangeTutor: () => void
  openDuplicate: () => void
  openMerge: () => void
  openDelete: () => void
  openCreateTask: () => Promise<void>
  openRelatedTasks: () => Promise<void>
}

export const useDisciplineActions = (): DisciplineActionsReturn => {
  const disciplineStore = useDisciplineStore()
  const generalInfoStore = useDisciplineGeneralInfoStore()
  const scheduleStore = useDisciplineScheduleStore()
  const listsStore = useListsStore()
  const { canManageDisciplines, canDeleteDisciplines } = useDisciplinePrivileges()

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const refIsTasksVisible = computed<boolean>(() =>
    !!listsStore.getLists?.tasks_kanban?.enabled
    && disciplineStore.getDetails?.customer?.status === CustomerStatus.Deal)

  const refHasTutor = computed<boolean>(() => !!disciplineStore.getDetails?.tutor_id)

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const openChangeTutor = (): void => {
    if (!refHasTutor.value) return

    isOpen.value = false
    scheduleStore.openFlow(DisciplineScheduleFlow.ChangeTutor)
  }

  const openModal = (modal: DisciplineGeneralModal): void => {
    isOpen.value = false
    generalInfoStore.setActiveModal(modal)
  }

  const openDuplicate = (): void => openModal(DisciplineGeneralModal.Duplicate)
  const openMerge = (): void => {
    generalInfoStore.setMergeIds([])
    openModal(DisciplineGeneralModal.Merge)
  }
  const openDelete = (): void => openModal(DisciplineGeneralModal.Delete)

  const openCreateTask = async (): Promise<void> => {
    const details = disciplineStore.getDetails
    if (!details?.customer) return

    isOpen.value = false
    await navigateTo({
      path: AppRoute.TasksKanbanCreation,
      query: {
        customer_id: details.customer.id,
        related_to_id: details.id,
        related_to_type: TasksKanbanRelatedToType.Discipline,
      },
    })
  }

  /** Passes the CUSTOMER id as `related_to_id`, exactly as the legacy page did. */
  const openRelatedTasks = async (): Promise<void> => {
    const customer = disciplineStore.getDetails?.customer
    if (!customer) return

    isOpen.value = false
    await navigateTo({
      path: AppRoute.TasksKanbanSupport,
      query: {
        related_to_id: customer.id,
        related_to_type: TasksKanbanRelatedToType.Discipline,
      },
    })
  }

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    canManageDisciplines,
    canDeleteDisciplines,
    refIsTasksVisible,
    refHasTutor,
    toggle,
    openChangeTutor,
    openDuplicate,
    openMerge,
    openDelete,
    openCreateTask,
    openRelatedTasks,
  }
}
